import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ParticleBackgroundComponent } from '../lib/components/general/particle-background/particle-background.component';
import { PasswordResetService } from './password-reset-service';

export type PasswordResetState = 'verifying' | 'invalid' | 'form' | 'success';

@Component({
	selector: 'app-password-reset-page',
	standalone: true,
	imports: [ReactiveFormsModule, FormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, ParticleBackgroundComponent],
	templateUrl: './password-reset.html',
	styleUrl: './password-reset.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordResetPage implements OnInit {
	private readonly fb = inject(FormBuilder);
	private readonly passwordResetService = inject(PasswordResetService);

	public code = input<string>('');

	public state = signal<PasswordResetState>('verifying');
	public submitting = signal(false);
	public hidePassword = signal(true);
	public hideConfirmPassword = signal(true);

	public userEmail = signal<string>('');

	public weakPassword = signal(false);
	public submitError = signal<string>('');

	public resetForm!: FormGroup;

	/** Captured from the query param on first read; the URL is scrubbed immediately after. */
	private capturedCode = '';

	private static readonly CODE_STORAGE_KEY = 'wp.passwordReset.code';

	constructor() {
		effect(() => {
			this.verifyCode(this.code());
		});
	}

	ngOnInit(): void {
		this.resetForm = this.fb.group(
			{
				password: [
					'',
					[
						Validators.required,
						Validators.minLength(8),
						this.patternValidator(/\d/, { hasNumber: true }),
						this.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
						this.patternValidator(/[a-z]/, { hasSmallCase: true }),
						this.patternValidator(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { hasSpecialCharacters: true }),
					],
				],
				confirmPassword: ['', Validators.required],
			},
			{ validators: this.passwordMatchValidator },
		);
	}

	public async onSubmit(): Promise<void> {
		if (this.resetForm.invalid || this.submitting()) return;

		this.weakPassword.set(false);
		this.submitError.set('');
		this.submitting.set(true);

		const newPassword = this.resetForm.get('password')?.value;

		try {
			await this.passwordResetService.confirmReset(this.capturedCode, newPassword);
			this.clearStashedCode();
			this.state.set('success');
		} catch (err) {
			this.handleSubmitFailure(err);
		} finally {
			this.submitting.set(false);
		}
	}

	public clearSubmitErrors(): void {
		if (this.weakPassword()) this.weakPassword.set(false);
		if (this.submitError()) this.submitError.set('');
	}

	private async verifyCode(rawCode: string): Promise<void> {
		// Falls back to the stash so a reload survives the URL having been scrubbed.
		const code = (rawCode ?? '').trim() || this.readStashedCode();

		if (!code) {
			this.state.set('invalid');
			return;
		}

		this.capturedCode = code;
		this.stashCode(code);
		this.scrubCodeFromUrl();
		this.state.set('verifying');

		return this.passwordResetService
			.validateCode(code)
			.then((result) => {
				this.userEmail.set(result.email ?? '');
				this.state.set('form');
			})
			.catch(() => {
				this.clearStashedCode();
				this.state.set('invalid');
			});
	}

	private handleSubmitFailure(err: unknown): void {
		const reason = typeof err === 'string' ? err : '';

		if (reason === 'weak_password') {
			this.weakPassword.set(true);
			return;
		}

		if (reason === 'invalid_or_expired') {
			this.clearStashedCode();
			this.state.set('invalid');
			return;
		}

		this.submitError.set('We could not update your password. Please try again.');
	}

	/**
	 * Drop the one-time code from the address bar and the current history entry once it has been
	 * captured. replaceState bypasses the Angular router, so the `code` input signal keeps its
	 * value and the effect does not re-run.
	 */
	private scrubCodeFromUrl(): void {
		if (typeof window === 'undefined' || !window.history?.replaceState) return;

		const url = new URL(window.location.href);
		if (!url.searchParams.has('code')) return;

		url.searchParams.delete('code');
		window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
	}

	private readStashedCode(): string {
		try {
			return sessionStorage.getItem(PasswordResetPage.CODE_STORAGE_KEY)?.trim() ?? '';
		} catch {
			return '';
		}
	}

	private stashCode(code: string): void {
		try {
			sessionStorage.setItem(PasswordResetPage.CODE_STORAGE_KEY, code);
		} catch {
			// Private mode or blocked storage - only costs reload recovery.
		}
	}

	private clearStashedCode(): void {
		try {
			sessionStorage.removeItem(PasswordResetPage.CODE_STORAGE_KEY);
		} catch {
			// Nothing was stashed.
		}
	}

	private patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!control.value) return null;
			const valid = regex.test(control.value);
			return valid ? null : error;
		};
	}

	private passwordMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
		const pass = group.get('password')?.value;
		const confirm = group.get('confirmPassword')?.value;
		return pass === confirm ? null : { notMatching: true };
	};

	get passwordControl() {
		return this.resetForm.get('password');
	}
	get confirmControl() {
		return this.resetForm.get('confirmPassword');
	}

	hasError(errorName: string): boolean {
		return (this.passwordControl?.hasError(errorName) && (this.passwordControl?.dirty || this.passwordControl?.touched)) || false;
	}
}
